import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import { motion, AnimatePresence } from 'framer-motion';
import { getCurrentUserProfile, getTeams, getUsers, getProfilePictureUrl, assignEmployeeToTeam, removeEmployeeFromTeam } from '../services/api';
import ProfilePicture from './ProfilePicture';

const ManagerTeamsPage = () => {
  const { t } = useTranslation('manager');
  const { t: tCommon } = useTranslation('common');
  const [currentUser, setCurrentUser] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamMembersMap, setTeamMembersMap] = useState({}); // Map of teamId -> members array
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTeams, setExpandedTeams] = useState(new Set()); // Track which teams are expanded
  const [loadingMembers, setLoadingMembers] = useState(new Set()); // Track which teams are loading members
  
  // Employees list: unassigned only or all (for assign/reassign)
  const [employeeListMode, setEmployeeListMode] = useState('unassigned'); // 'unassigned' | 'all'
  const [allUnassignedEmployees, setAllUnassignedEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [loadingAllEmployees, setLoadingAllEmployees] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  
  // Employee assignment state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignmentMessage, setAssignmentMessage] = useState(null);
  const [removeModal, setRemoveModal] = useState({ isOpen: false, employeeId: null, teamId: null, employeeName: null });
  const [removingEmployeeId, setRemovingEmployeeId] = useState(null);
  const [removalMessage, setRemovalMessage] = useState(null);

  // Source list for assign table: unassigned only or all employees
  const assignSourceList = employeeListMode === 'all' ? allEmployees : allUnassignedEmployees;
  // Filter by search (name or email)
  const filteredUnassignedEmployees = useMemo(() => {
    if (!employeeSearchQuery.trim()) return assignSourceList;
    const q = employeeSearchQuery.trim().toLowerCase();
    return assignSourceList.filter(
      (emp) =>
        (emp.first_name && emp.first_name.toLowerCase().includes(q)) ||
        (emp.last_name && emp.last_name.toLowerCase().includes(q)) ||
        (emp.email && emp.email.toLowerCase().includes(q)) ||
        `${(emp.first_name || '').toLowerCase()} ${(emp.last_name || '').toLowerCase()}`.trim().includes(q) ||
        `${(emp.last_name || '').toLowerCase()} ${(emp.first_name || '').toLowerCase()}`.trim().includes(q)
    );
  }, [assignSourceList, employeeSearchQuery]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch current user profile to get manager ID
        const userData = await getCurrentUserProfile();
        setCurrentUser(userData);

        // Fetch all platform teams (managers can manage all teams)
        if (userData.id) {
          try {
            const teamsData = await getTeams({ status: 'active' }, 1, 500);
            const teamsList = Array.isArray(teamsData) 
              ? teamsData 
              : (teamsData.results || []);
            setTeams(teamsList);
          } catch (err) {
            console.error('Failed to fetch teams:', err);
            setError(t('teamsPage.loadFailed'));
            setTeams([]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch user data:', err);
        setError(t('teamsPage.loadFailedInfo'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && removeModal.isOpen) {
        handleRemoveCancel();
      }
    };

    if (removeModal.isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [removeModal.isOpen]);

  // Fetch team members when a team is expanded (forceRefresh = true bypasses cache after assign/remove)
  const fetchTeamMembers = async (teamId, forceRefresh = false) => {
    if (!forceRefresh && teamMembersMap[teamId]) {
      return;
    }

    // #region agent log
    // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerTeamsPage.js:fetchTeamMembers','message':'fetchTeamMembers called',data:{teamId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion

    setLoadingMembers(prev => new Set(prev).add(teamId));
    try {
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerTeamsPage.js:fetchTeamMembers','message':'Calling getUsers API',data:{teamId,status:'active'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      
      const membersData = await getUsers({ team: teamId, status: 'active' }, 1, 100);
      
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerTeamsPage.js:fetchTeamMembers','message':'getUsers API response received',data:{teamId,isArray:Array.isArray(membersData),hasResults:!!membersData?.results,resultsCount:Array.isArray(membersData)?membersData.length:(membersData?.results?.length||0),error:membersData?.error,status:membersData?.status},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
      // #endregion
      
      const membersList = Array.isArray(membersData)
        ? membersData
        : (membersData.results || []);
      
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerTeamsPage.js:fetchTeamMembers','message':'Setting team members',data:{teamId,membersCount:membersList.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
      // #endregion
      
      setTeamMembersMap(prev => ({
        ...prev,
        [teamId]: membersList
      }));
    } catch (err) {
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerTeamsPage.js:fetchTeamMembers','message':'getUsers API error',data:{teamId,errorMessage:err?.message,errorStatus:err?.status,errorData:err?.data},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      // #endregion
      
      console.error(`Failed to fetch members for team ${teamId}:`, err);
      setTeamMembersMap(prev => ({
        ...prev,
        [teamId]: []
      }));
    } finally {
      setLoadingMembers(prev => {
        const newSet = new Set(prev);
        newSet.delete(teamId);
        return newSet;
      });
    }
  };

  // Toggle team expansion
  const toggleTeamExpansion = (teamId) => {
    setExpandedTeams(prev => {
      const newSet = new Set(prev);
      if (newSet.has(teamId)) {
        newSet.delete(teamId);
      } else {
        newSet.add(teamId);
        // Fetch members if not already loaded
        if (!teamMembersMap[teamId]) {
          fetchTeamMembers(teamId);
        }
      }
      return newSet;
    });
  };

  // Helper function to get user initials
  const getUserInitials = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user.first_name) {
      return user.first_name[0].toUpperCase();
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return '?';
  };

  // Helper function to get user display name
  const getUserDisplayName = (user) => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.email || t('teamsPage.unknown');
  };

  // Fetch unassigned employees only
  const fetchUnassignedEmployees = async () => {
    setLoadingUnassigned(true);
    try {
      const data = await getUsers(
        { role: 'EMPLOYE', status: 'active', team: 'null' },
        1,
        500
      );
      const list = Array.isArray(data) ? data : (data.results || []);
      const noTeam = list.filter((emp) => !emp.team || emp.team === null);
      setAllUnassignedEmployees(noTeam);
    } catch (err) {
      console.error('Failed to fetch unassigned employees:', err);
      setAllUnassignedEmployees([]);
    } finally {
      setLoadingUnassigned(false);
    }
  };

  // Fetch all employees (for reassignment)
  const fetchAllEmployees = async () => {
    setLoadingAllEmployees(true);
    try {
      const data = await getUsers(
        { role: 'EMPLOYE', status: 'active' },
        1,
        1000
      );
      const list = Array.isArray(data) ? data : (data.results || []);
      setAllEmployees(list);
    } catch (err) {
      console.error('Failed to fetch all employees:', err);
      setAllEmployees([]);
    } finally {
      setLoadingAllEmployees(false);
    }
  };

  useEffect(() => {
    if (teams.length > 0) {
      fetchUnassignedEmployees();
    } else {
      setAllUnassignedEmployees([]);
    }
  }, [teams.length]);

  useEffect(() => {
    if (teams.length > 0 && employeeListMode === 'all') {
      fetchAllEmployees();
    } else if (employeeListMode !== 'all') {
      setAllEmployees([]);
    }
  }, [teams.length, employeeListMode]);

  // Handle employee assignment
  const handleAssignEmployee = async () => {
    if (!selectedEmployee || !selectedTeamId) {
      setAssignmentMessage({
        type: 'error',
        text: t('teamsPage.selectBoth')
      });
      return;
    }

    setAssigning(true);
    setAssignmentMessage(null);

    try {
      const response = await assignEmployeeToTeam(selectedEmployee.id, parseInt(selectedTeamId));
      
      setAssignmentMessage({
        type: 'success',
        text: response.message || `Employee ${selectedEmployee.email} has been assigned successfully.`
      });

      // Clear selections
      setSelectedEmployee(null);
      setSelectedTeamId('');

      // Refresh employee lists and teams
      fetchUnassignedEmployees();
      if (employeeListMode === 'all') {
        fetchAllEmployees();
      }

      // Refresh all teams to show updated member counts
      try {
        const teamsData = await getTeams({ status: 'active' }, 1, 500);
        const teamsList = Array.isArray(teamsData) 
          ? teamsData 
          : (teamsData.results || []);
        setTeams(teamsList);
        fetchTeamMembers(parseInt(selectedTeamId), true);
      } catch (err) {
        console.error('Failed to refresh teams:', err);
      }

      // Clear success message after 5 seconds
      setTimeout(() => {
        setAssignmentMessage(null);
      }, 5000);
    } catch (err) {
      console.error('Failed to assign employee:', err);
      const errorMessage = err?.data?.error || err?.message || t('teamsPage.assignFailed');
      setAssignmentMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setAssigning(false);
    }
  };

  // Open remove confirmation modal
  const handleRemoveEmployee = (employeeId, teamId, employeeName) => {
    setRemoveModal({
      isOpen: true,
      employeeId,
      teamId,
      employeeName
    });
  };

  // Cancel removal
  const handleRemoveCancel = () => {
    setRemoveModal({ isOpen: false, employeeId: null, teamId: null, employeeName: null });
  };

  // Confirm and perform removal
  const handleRemoveConfirm = async () => {
    const { employeeId, teamId } = removeModal;
    
    if (!employeeId || !teamId) {
      return;
    }

    setRemovingEmployeeId(employeeId);
    setRemovalMessage(null);
    setRemoveModal({ isOpen: false, employeeId: null, teamId: null, employeeName: null });

    try {
      const response = await removeEmployeeFromTeam(employeeId, teamId);
      
      setRemovalMessage({
        type: 'success',
        text: response.message || `Employee has been removed from the team successfully.`
      });

      // Refresh team members so the list updates without reload
      fetchTeamMembers(teamId, true);

      // Refresh unassigned employees so the removed employee appears in the table
      fetchUnassignedEmployees();

      // Refresh all teams to show updated member counts
      try {
        const teamsData = await getTeams({ status: 'active' }, 1, 500);
        const teamsList = Array.isArray(teamsData) 
          ? teamsData 
          : (teamsData.results || []);
        setTeams(teamsList);
      } catch (err) {
        console.error('Failed to refresh teams:', err);
      }

      // Clear success message after 5 seconds
      setTimeout(() => {
        setRemovalMessage(null);
      }, 5000);
    } catch (err) {
      console.error('Failed to remove employee:', err);
      const errorMessage = err?.data?.error || err?.message || t('teamsPage.removeFailed');
      setRemovalMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setRemovingEmployeeId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-text-secondary">{t('teamsPage.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !currentUser) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <span className="material-symbols-outlined text-red-500 text-4xl mb-4">error</span>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold text-text-main">{t('teamsPage.title')}</h1>
        <p className="text-text-secondary">
          {teams.length > 0 ? (
            <>View and manage all {teams.length} {teams.length === 1 ? 'team' : 'teams'} on the platform</>
          ) : (
            t('teamsPage.noActiveTeams')
          )}
        </p>
      </div>

      {/* Assign Employees to Teams Section */}
      {teams.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-[#e5e7eb] shadow-sm p-6"
        >
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="text-xl font-bold text-text-main mb-2">{t('teamsPage.assignTitle')}</h2>
              <p className="text-sm text-text-secondary">
                Any manager can assign any employee to any team. Search and select an employee, then choose a team.
              </p>
            </div>

            {/* Toggle: Unassigned only vs All employees (for reassignment) */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-text-main">{t('teamsPage.showEmployees')}</span>
              <div className="flex rounded-lg border border-[#e5e7eb] dark:border-slate-600 p-0.5 bg-gray-50 dark:bg-slate-800">
                <button
                  type="button"
                  onClick={() => setEmployeeListMode('unassigned')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    employeeListMode === 'unassigned'
                      ? 'bg-white text-primary shadow-sm border border-gray-200'
                      : 'text-text-secondary hover:text-text-main'
                  }`}
                >
                  Unassigned only
                </button>
                <button
                  type="button"
                  onClick={() => setEmployeeListMode('all')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    employeeListMode === 'all'
                      ? 'bg-white text-primary shadow-sm border border-gray-200'
                      : 'text-text-secondary hover:text-text-main'
                  }`}
                >
                  All employees
                </button>
              </div>
            </div>

            {/* Assignment Message */}
            {assignmentMessage && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-lg ${
                  assignmentMessage.type === 'success'
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200'
                    : 'bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-[20px]">
                    {assignmentMessage.type === 'success' ? 'check_circle' : 'error'}
                  </span>
                  <span className="text-sm font-medium">{assignmentMessage.text}</span>
                </div>
              </motion.div>
            )}

            {/* Search Input - filters the unassigned employees table below */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-text-main">{t('teamsPage.searchEmployees')}</label>
              <div className="relative">
                <input
                  type="text"
                  value={employeeSearchQuery}
                  onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                  placeholder={t('teamsPage.searchPlaceholder')}
                  className="w-full h-10 rounded-lg border border-[#dbe0e6] dark:border-slate-600 bg-white dark:bg-slate-700 px-4 pr-10 text-sm text-text-main dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary placeholder-slate-500 dark:placeholder-slate-400"
                />
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-[#617289] text-[20px] pointer-events-none">search</span>
              </div>
            </div>

            {/* Table of employees (filtered by search) */}
            <div className="border border-[#e5e7eb] rounded-lg overflow-hidden">
              {(employeeListMode === 'unassigned' ? loadingUnassigned : loadingAllEmployees) ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2"></div>
                    <p className="text-sm text-text-secondary">
                      {employeeListMode === 'unassigned' ? t('teamsPage.loadingUnassigned') : t('teamsPage.loadingAll')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-[#f9fafb] border-b border-[#e5e7eb] text-text-secondary uppercase tracking-wide">
                      <tr>
                        <th scope="col" className="px-4 py-3 font-semibold rounded-tl-lg">{t('teamsPage.employee')}</th>
                        <th scope="col" className="px-4 py-3 font-semibold">{t('teamsPage.email')}</th>
                        {employeeListMode === 'all' && (
                          <th scope="col" className="px-4 py-3 font-semibold">{t('teamsPage.currentTeam')}</th>
                        )}
                        <th scope="col" className="px-4 py-3 font-semibold rounded-tr-lg text-right w-32">{t('teamsPage.action')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#e5e7eb]">
                      {filteredUnassignedEmployees.length > 0 ? (
                        filteredUnassignedEmployees.map((employee) => (
                          <tr
                            key={employee.id}
                            className={`bg-white hover:bg-gray-50 transition-colors ${
                              selectedEmployee?.id === employee.id ? 'bg-primary/5 ring-1 ring-primary/20' : ''
                            }`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <ProfilePicture
                                  profilePictureUrl={employee.profile_picture ? getProfilePictureUrl(employee.profile_picture) : null}
                                  initials={getUserInitials(employee)}
                                  size="sm"
                                />
                                <span className="font-medium text-text-main">
                                  {getUserDisplayName(employee)}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-text-secondary">{employee.email || '—'}</td>
                            {employeeListMode === 'all' && (
                              <td className="px-4 py-3 text-text-secondary">
                                {employee.team?.name ? (
                                  <span className="inline-flex items-center gap-1">
                                    <span className="material-symbols-outlined text-[18px]">groups</span>
                                    {employee.team.name}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                            )}
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => setSelectedEmployee(employee)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                  selectedEmployee?.id === employee.id
                                    ? 'bg-primary text-white'
                                    : 'bg-gray-100 text-text-main hover:bg-primary/10 hover:text-primary'
                                }`}
                              >
                                {selectedEmployee?.id === employee.id ? (
                                  <>
                                    <span className="material-symbols-outlined text-[18px]">check_circle</span>
                                    <span>{t('teamsPage.selected')}</span>
                                  </>
                                ) : (
                                  <>
                                    <span className="material-symbols-outlined text-[18px]">person_add</span>
                                    <span>{employee.team ? t('teamsPage.reassign') : t('teamsPage.select')}</span>
                                  </>
                                )}
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={employeeListMode === 'all' ? 4 : 3} className="px-4 py-12 text-center text-text-secondary">
                            <span className="material-symbols-outlined text-gray-400 text-4xl mb-2 block">person_off</span>
                            <p className="text-sm">
                              {employeeSearchQuery.trim()
                                ? tCommon('noResults')
                                : employeeListMode === 'all'
                                  ? t('teamsPage.noEmployees')
                                  : t('teamsPage.noUnassigned')}
                            </p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Team Selection and Assign Button */}
            {selectedEmployee && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="flex flex-col gap-4 pt-4 border-t border-[#e5e7eb]"
              >
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-text-main">{t('teamsPage.selectTeam')}</label>
                  <select
                    value={selectedTeamId}
                    onChange={(e) => setSelectedTeamId(e.target.value)}
                    className="w-full h-10 rounded-lg border border-[#dbe0e6] dark:border-slate-600 bg-white dark:bg-slate-700 px-4 text-sm text-text-main dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  >
                    <option value="">{t('teamsPage.chooseTeam')}</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name} ({team.centre?.name || t('teamsPage.noCentre')})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleAssignEmployee}
                    disabled={!selectedTeamId || assigning}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {assigning ? (
                      <>
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>{t('teamsPage.assigning')}</span>
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-[18px]">person_add</span>
                        <span>{t('teamsPage.assignToTeam')}</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedEmployee(null);
                      setSelectedTeamId('');
                      setEmployeeSearchQuery('');
                      // setEmployeesWithoutTeam ([]);
                      setAssignmentMessage(null);
                    }}
                    className="px-4 py-2 text-text-secondary text-sm font-medium hover:text-text-main transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}

      {/* Teams List */}
      {teams.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {teams.map((team) => {
            const isExpanded = expandedTeams.has(team.id);
            const members = teamMembersMap[team.id] || [];
            const isLoadingMembers = loadingMembers.has(team.id);
            const memberCount = team.member_count || members.length || 0;

            return (
              <motion.div
                key={team.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-slate-800 rounded-xl border border-[#e5e7eb] dark:border-slate-600 shadow-sm overflow-hidden"
              >
                {/* Team Header */}
                <div
                  className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                  onClick={() => toggleTeamExpansion(team.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="bg-primary/10 dark:bg-primary/20 p-3 rounded-lg">
                        <span className="material-symbols-outlined text-primary text-2xl">groups</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold text-text-main dark:text-slate-100 mb-1">
                          {team.name || 'Unnamed Team'}{' '}
                          <small className="text-xs text-text-secondary dark:text-slate-400">
                            Managed by{" "}
                            <b>
                              {(() => {
                                const managers = team.managers && Array.isArray(team.managers) ? team.managers : [];
                                const names = managers.filter(Boolean).map(m => m.id === currentUser?.id ? 'you' : `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email).filter(Boolean);
                                return names.length ? names.join(', ') : t('teamsPage.noManager');
                              })()}
                            </b>
                          </small>
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-text-secondary dark:text-slate-400">
                          {/* <div className="flex items-center gap-1">
                            <span className="material-symbols-outlined text-[18px]">location_city</span>
                            <span>{team.centre?.name || t('teamsPage.noCentre')}</span>
                          </div> */}
                          <div className="flex items-center gap-1">
                            <span className="material-symbols-outlined text-[18px]">people</span>
                            <span>{memberCount} {memberCount === 1 ? 'member' : 'members'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <motion.span
                      className="material-symbols-outlined text-text-secondary dark:text-slate-400 text-2xl transition-transform"
                      animate={{ rotate: isExpanded ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      expand_more
                    </motion.span>
                  </div>
                </div>

                {/* Team Members (Expanded) */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-[#e5e7eb] dark:border-slate-600 overflow-hidden"
                    >
                      <div className="p-6 bg-gray-50 dark:bg-slate-700/30">
                        <h4 className="text-sm font-semibold text-text-secondary dark:text-slate-400 uppercase tracking-wide mb-4">
                          Team Members
                        </h4>
                        
                        {/* Removal Message */}
                        {removalMessage && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`p-4 rounded-lg mb-4 ${
                              removalMessage.type === 'success'
                                ? 'bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200'
                                : 'bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="material-symbols-outlined text-[20px]">
                                {removalMessage.type === 'success' ? 'check_circle' : 'error'}
                              </span>
                              <span className="text-sm font-medium">{removalMessage.text}</span>
                            </div>
                          </motion.div>
                        )}
                        
                        {isLoadingMembers ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="text-center">
                              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2"></div>
                              <p className="text-sm text-text-secondary dark:text-slate-400">{t('teamsPage.loadingMembers')}</p>
                            </div>
                          </div>
                        ) : members.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {members.map((member) => (
                              <motion.div
                                key={member.id}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex items-center gap-3 p-4 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-600 hover:shadow-md dark:hover:bg-slate-700/50 transition-shadow"
                              >
                                <ProfilePicture
                                  profilePictureUrl={member.profile_picture ? getProfilePictureUrl(member.profile_picture) : null}
                                  initials={getUserInitials(member)}
                                  size="md"
                                />
                                <div className="flex-1 min-w-0">
                                  <h5 className="text-base font-semibold text-text-main dark:text-slate-100 truncate">
                                    {getUserDisplayName(member)}
                                  </h5>
                                  <p className="text-sm text-text-secondary dark:text-slate-400 truncate">{member.email}</p>
                                  {member.role_display && (
                                    <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-slate-200 rounded-full">
                                      {member.role_display}
                                    </span>
                                  )}
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveEmployee(member.id, team.id, getUserDisplayName(member));
                                  }}
                                  disabled={removingEmployeeId === member.id}
                                  className="flex items-center justify-center w-8 h-8 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/30 dark:hover:text-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={t('teamsPage.removeFromTeam')}
                                >
                                  {removingEmployeeId === member.id ? (
                                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 dark:border-red-400"></div>
                                  ) : (
                                    <span className="material-symbols-outlined text-[20px]">person_remove</span>
                                  )}
                                </button>
                              </motion.div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <span className="material-symbols-outlined text-gray-400 dark:text-slate-500 text-4xl mb-2">person_off</span>
                            <p className="text-text-secondary dark:text-slate-400">{t('teamsPage.noMembers')}</p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-[#e5e7eb] dark:border-slate-600 shadow-sm p-12">
          <div className="text-center">
            <span className="material-symbols-outlined text-gray-400 dark:text-slate-500 text-6xl mb-4">groups_off</span>
            <h3 className="text-xl font-semibold text-text-main dark:text-slate-100 mb-2">{t('teamsPage.noTeams')}</h3>
            <p className="text-text-secondary dark:text-slate-400">
              There are no active teams on the platform.
            </p>
          </div>
        </div>
      )}

      {/* Remove Employee Confirmation Modal */}
      <AnimatePresence>
        {removeModal.isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={handleRemoveCancel}
            />
            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-md w-full border border-slate-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                      <span className="material-symbols-outlined text-red-600 text-[24px]">
                        warning
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900 mb-2">
                        Remove Employee from Team
                      </h3>
                      <p className="text-sm text-slate-600 mb-1">
                        Are you sure you want to remove <span className="font-semibold text-slate-900">"{removeModal.employeeName || 'this employee'}"</span> from the team?
                      </p>
                      <p className="text-sm text-red-600 font-medium">
                        This action cannot be undone.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-3 mt-6">
                    <button
                      onClick={handleRemoveCancel}
                      disabled={removingEmployeeId !== null}
                      className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRemoveConfirm}
                      disabled={removingEmployeeId !== null}
                      className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {removingEmployeeId !== null ? (
                        <>
                          <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>{t('teamsPage.removing')}</span>
                        </>
                      ) : (
                        <span>{t('teamsPage.remove')}</span>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ManagerTeamsPage;

